import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config';
import DashboardLayout from '../components/DashboardLayout';
import affiliateService from '../services/affiliateService';


function Profile() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [usage, setUsage] = useState(null);
  const [profile, setProfile] = useState(null);
  const [affiliateData, setAffiliateData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (user?.role === 'affiliate') {
          console.log('Fetching affiliate profile for user:', user);
          // Fetch affiliate-specific profile
          const affiliateProfile = await affiliateService.getProfile();
          console.log('Affiliate profile response:', affiliateProfile);
          setProfile(affiliateProfile);
          setAffiliateData(affiliateProfile.affiliateData);
          setFormData(affiliateProfile.affiliateData || {});
        } else {
          console.log('Fetching regular profile for user:', user);
          // Fetch regular profile
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setProfile(response.data);
          setUsage(response.data.usage);
        }
      } catch (err) {
        console.error('Profile fetch error:', err);
        console.error('Error response:', err.response?.data);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [token, user?.role]);

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSaveAffiliateData = async () => {
    try {
      setLoading(true);
      await affiliateService.updateProfile(formData);
      setAffiliateData(formData);
      setIsEditing(false);
      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData(affiliateData || {});
    setIsEditing(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Banned':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Alert severity="warning">You must be signed in to view your profile.</Alert>
      </Container>
    );
  }

  return (
    <DashboardLayout title="Profile">
      <Container maxWidth="md" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Profile Information
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Email: <strong>{user.email}</strong>
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Role: <strong>{user.role}</strong>
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {user?.role === 'affiliate' && affiliateData ? (
            <>
              {/* Affiliate Status Card */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        Affiliate Status
                      </Typography>
                      <Chip 
                        label={affiliateData.status || 'Pending'} 
                        color={getStatusColor(affiliateData.status || 'Pending')}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {affiliateData.status === 'Pending' && 'Your application is under review.'}
                      {affiliateData.status === 'Active' && 'Your affiliate account is active.'}
                      {affiliateData.status === 'Banned' && 'Your affiliate account has been suspended.'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Affiliate Information Card */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h6">
                        Affiliate Information
                      </Typography>
                      {!isEditing ? (
                        <Button 
                          variant="outlined" 
                          onClick={() => setIsEditing(true)}
                        >
                          Edit
                        </Button>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            variant="contained" 
                            onClick={handleSaveAffiliateData}
                            disabled={loading}
                          >
                            Save
                          </Button>
                          <Button 
                            variant="outlined" 
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        </Box>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="Company *"
                            value={formData.company || ''}
                            onChange={handleInputChange('company')}
                            required
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>Company:</strong> {affiliateData.company || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="Name *"
                            value={formData.name || ''}
                            onChange={handleInputChange('name')}
                            required
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>Name:</strong> {affiliateData.name || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="Address"
                            value={formData.address || ''}
                            onChange={handleInputChange('address')}
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>Address:</strong> {affiliateData.address || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="City"
                            value={formData.city || ''}
                            onChange={handleInputChange('city')}
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>City:</strong> {affiliateData.city || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="Country"
                            value={formData.country || ''}
                            onChange={handleInputChange('country')}
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>Country:</strong> {affiliateData.country || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="Phone"
                            value={formData.phone || ''}
                            onChange={handleInputChange('phone')}
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>Phone:</strong> {affiliateData.phone || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="Best DM Method"
                            value={formData.bestDM || ''}
                            onChange={handleInputChange('bestDM')}
                            placeholder="e.g., Instagram, Email, TikTok"
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>Best DM:</strong> {affiliateData.bestDM || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="Instagram"
                            value={formData.instagram || ''}
                            onChange={handleInputChange('instagram')}
                            placeholder="@username"
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>Instagram:</strong> {affiliateData.instagram || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="TikTok"
                            value={formData.tiktok || ''}
                            onChange={handleInputChange('tiktok')}
                            placeholder="@username"
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>TikTok:</strong> {affiliateData.tiktok || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="Facebook"
                            value={formData.facebook || ''}
                            onChange={handleInputChange('facebook')}
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>Facebook:</strong> {affiliateData.facebook || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="Other Social Media"
                            value={formData.other || ''}
                            onChange={handleInputChange('other')}
                            placeholder="YouTube, LinkedIn, etc."
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>Other Social:</strong> {affiliateData.other || '-'}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            label="Payment Details"
                            value={formData.paymentDetails || ''}
                            onChange={handleInputChange('paymentDetails')}
                            multiline
                            rows={2}
                            placeholder="PayPal email, bank details, etc."
                          />
                        ) : (
                          <Typography variant="body1">
                            <strong>Payment Details:</strong> {affiliateData.paymentDetails || '-'}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : user?.role !== 'affiliate' && (
            <Grid item xs={12}>
              <Card sx={{ bgcolor: 'background.paper' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Usage Statistics
                  </Typography>
                  {usage && (
                    <>
                      <Typography variant="body1" gutterBottom>
                        Generation Count: <strong>{usage.generationCount}</strong>
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        Last Reset: <strong>{new Date(usage.lastResetDate).toLocaleDateString()}</strong>
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </DashboardLayout>
  );
}

export default Profile; 